import {
  Button,
  Flex,
  Modal,
  SwitchField,
  Typography,
} from '@iheartjane/reefer';
import type { Overflow } from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type {
  Control,
  FieldValues,
  Path,
  SubmitHandler,
  UseFormReturn,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { businessPaths } from '@jane/business-admin/util';
import { ConfirmChangeModal } from '@jane/shared/components';

import styles from './ModalPageWrapper.module.css';

type PreviewButtonProps = {
  disable?: boolean;
  label?: string;
  loading?: boolean;
  onClick: () => void;
};

export interface ModalPageWrapperProps<T extends FieldValues> {
  centerContent?: ReactNode;
  children: ReactNode;
  formMethods: UseFormReturn<T>;
  isLoading?: boolean;
  modalOverflow?: Overflow;
  onSubmit: SubmitHandler<T>;
  preview?: PreviewButtonProps;
  secondaryAction?: ReactNode;
  submitLabel?: string;
  title: string;
  toggle?: {
    control: Control<T>;
    label: string;
    name: Path<T>;
  };
}

const useForceFallback = () => {
  const navigate = useNavigate();
  return useCallback(() => {
    navigate(businessPaths.premium(), { replace: true });
  }, [navigate]);
};

export const ModalPageWrapper = <T extends FieldValues>({
  centerContent,
  children,
  formMethods,
  modalOverflow,
  isLoading,
  onSubmit,
  preview,
  secondaryAction,
  submitLabel,
  title,
  toggle,
}: ModalPageWrapperProps<T>) => {
  const goBack = useForceFallback();
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const {
    formState: { isDirty, isValid },
  } = formMethods;

  const onRequestClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      goBack();
    }
  }, [isDirty, goBack]);

  const publishable = useMemo(() => {
    return isDirty && isValid && !isLoading;
  }, [isDirty, isValid, isLoading]);

  return (
    <>
      <Modal
        appId="parent"
        open
        variant="full-screen"
        onRequestClose={onRequestClose}
        overflow={modalOverflow ?? 'auto'}
      >
        <Form.BaseForm
          formMethods={formMethods}
          name={`${title.toLowerCase().split(' ').join('-')}-form`}
          onSubmit={onSubmit}
        >
          <Modal.Header
            title={title}
            actions={
              <Flex ml="auto" flexDirection="row">
                {toggle && (
                  <Controller
                    control={toggle.control}
                    name={toggle.name}
                    render={({ field }) => (
                      <SwitchField
                        label={toggle.label || 'Visible'}
                        name="toggle"
                        checked={field.value}
                        onChange={field.onChange}
                        className={styles['switchField']}
                      />
                    )}
                  />
                )}

                {secondaryAction}

                {preview ? (
                  <Button
                    ml="auto"
                    mr={16}
                    label={preview.label || 'Preview'}
                    type="button"
                    variant="secondary"
                    onClick={preview.onClick}
                    disabled={preview.disable || !isValid}
                    loading={preview.loading}
                  />
                ) : null}

                <Form.SubmitButton
                  ml="auto"
                  label={submitLabel ?? 'Publish'}
                  variant="primary"
                  disabled={!publishable}
                  loading={isLoading}
                />
              </Flex>
            }
            centerContent={centerContent}
          />
          {children}
        </Form.BaseForm>
      </Modal>

      <ConfirmChangeModal
        open={showDiscardModal}
        title={
          <Typography variant="header-bold" mb={8} as="div">
            Discard changes?
          </Typography>
        }
        onConfirm={() => goBack()}
        onCancel={() => setShowDiscardModal(false)}
        confirmButtonVariant="destructive"
        confirmLabel="Discard"
        subtitle="You have made changes without publishing. If you continue, you will lose your progress."
      />
    </>
  );
};
